<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'WAttachmentModal',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    attachment: {
      type: Object,
      required: false,
    },
    object_id: {
      type: String,
      required: false,
    },
    defer: {
      type: Boolean,
      required: false,
      default: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const formId = ref(null);
  const item = ref(null);
  const uploadTitle = ref($fn.value.pgettext('Attachments', 'Upload file'));
  const editTitle = ref($fn.value.pgettext('Attachments', 'Edit description'));
  const file = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));
  const b_7789867021953041_validation = computed(() => ['required']);

  const $data = computed(() => ({
    formId: formId.value,
    item: item.value,
    uploadTitle: uploadTitle.value,
    editTitle: editTitle.value,
    file: file.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_7789867021953041_validation: b_7789867021953041_validation.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = async () => {
    return (
      props.attachment || { object_id: props.object_id, description: null }
    );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'item' },
      { name: 'uploadTitle' },
      { name: 'editTitle' },
      { name: 'file' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_7789867021953041_validation', nodeId: '7789867021953041' },
    ],
    loaders: { formId: $$load_formId, item: $$load_item },
    ux: {},
    dataRefs: {
      formId,
      item,
      uploadTitle,
      editTitle,
      file,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_7789867021953041_validation,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return { title: props.attachment ? editTitle.value : uploadTitle.value };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.attachment,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('attachment');
    }
  );
  watch(
    () => props.object_id,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('object_id');
    }
  );
  watch(
    () => props.defer,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('defer');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_n7ts620ql_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_yx3cslat1_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
      },
    };
  };
  const act_1040167445267876_request_hz9ypg6m2_getActionArgs = async (
    $event
  ) => {
    return {
      operation: props.attachment ? 'update_attachment' : 'create_attachment',
      data: props.attachment ? item.value : { ...item.value, file: file.value },
      contentType: props.attachment
        ? 'application/json'
        : 'multipart/form-data',
    };
  };
  const inst_evh_1040167445267876_submit_rrwwc2wxo_getWhen = async ($event) => {
    return !props.defer;
  };
  const act_1040167445267876_closeModal_gqs83c403_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        value: { file: file.value, description: item.value.description },
      },
    };
  };
  const inst_evh_1040167445267876_submit_dn6yuoahq_getWhen = async ($event) => {
    return props.defer;
  };
  const act_2434514588044777_closeModal_gpcbqxovj_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
      },
    };
  };

  const act_7315092382398562_closeModal_n7ts620ql =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_n7ts620ql_getActionArgs,
    });
  const inst_evh_7315092382398562_close_amt2dtkqd =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_n7ts620ql,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_amt2dtkqd],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_8xf0l8myy =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_acyz43ccp =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_8xf0l8myy,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_acyz43ccp],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_eocxzbj7m =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_1316xzzg6 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_eocxzbj7m,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_1316xzzg6],
    event: 'close',
  });
  const act_1040167445267876_closeModal_yx3cslat1 =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_yx3cslat1_getActionArgs,
    });
  const inst_evh_1040167445267876_success_ttysc01e5 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_yx3cslat1,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_request_hz9ypg6m2 = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_hz9ypg6m2_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_ttysc01e5],
  });
  const inst_evh_1040167445267876_submit_rrwwc2wxo =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_hz9ypg6m2,
      event: 'submit',
      when: inst_evh_1040167445267876_submit_rrwwc2wxo_getWhen,
      displayName: 'save',
    });
  const act_1040167445267876_closeModal_gqs83c403 =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_gqs83c403_getActionArgs,
    });
  const inst_evh_1040167445267876_submit_dn6yuoahq =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_gqs83c403,
      event: 'submit',
      when: inst_evh_1040167445267876_submit_dn6yuoahq_getWhen,
      displayName: 'closeModal',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_1040167445267876_submit_rrwwc2wxo,
      inst_evh_1040167445267876_submit_dn6yuoahq,
    ],
    event: 'submit',
  });
  const act_2434514588044777_closeModal_gpcbqxovj =
    new actionTypes.CloseModalAction({
      actionArgs: act_2434514588044777_closeModal_gpcbqxovj_getActionArgs,
    });
  const inst_evh_2434514588044777_clickPreventStop_qx77n1fp9 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeModal_gpcbqxovj,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_qx77n1fp9],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_amt2dtkqd,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_acyz43ccp,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_1316xzzg6,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_ttysc01e5,
    inst_evh_1040167445267876_submit_rrwwc2wxo,
    save: inst_evh_1040167445267876_submit_rrwwc2wxo,
    inst_evh_1040167445267876_submit_dn6yuoahq,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_qx77n1fp9,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_amt2dtkqd = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_amt2dtkqd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_acyz43ccp = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_acyz43ccp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_1316xzzg6 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_1316xzzg6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_ttysc01e5 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_ttysc01e5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_rrwwc2wxo = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_rrwwc2wxo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_dn6yuoahq = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_dn6yuoahq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_qx77n1fp9 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_qx77n1fp9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="WAttachmentModal"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        name="attachment"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup
          name="file"
          :label="pgettext('Attachment', 'File')"
          labelFor="file"
          required
          v-if="!attachment"
        >
          <ItpFormFile
            :autofocus="!attachment"
            v-model="file"
            :value="file"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (file =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            name="file"
            required
            :placeholder="gettext('Choose a file or drop it here')"
            :drop-placeholder="gettext('Drop file here')"
            lang="hu"
            :validation="b_7789867021953041_validation"
          >
          </ItpFormFile>
        </ItpFormGroup>
        <ItpFormGroup name="description" :label="gettext('Description')">
          <ItpFormTextarea
            name="description"
            v-model="item.description"
            :value="item.description"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.description =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :autofocus="!!attachment"
          >
          </ItpFormTextarea>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          :disabled="!forms.attachment || !forms.attachment.submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
